/**
 * Billing on the job: its own rates (a rate set copied from the rate card and adjusted, used when the job has no
 * signed quote) and its extras (one-off charges such as plans, permits, an arrow board or TMA hire, billed once).
 * The office edits both directly under RLS. A job billing without a signed quote or a rate set shows a warning.
 */
import { useState } from "react";
import { Icon } from "../../components/Icon";
import { Notice, useNotice } from "../../components/Notice";
import { EmptyState, ErrorState, SkeletonLines } from "../../components/States";
import { BRAND } from "../../lib/config";
import { currentRateCard } from "../../lib/data";
import { aud } from "../../lib/format";
import { PREVIEW } from "../../lib/preview";
import { supabase, type JobDetail } from "../../lib/supabase";
import { useAsync } from "../../lib/useAsync";

interface CrewRate { crewSize: number; day: number; night: number; weekend: number; publicHoliday: number; travelAllowance: number }
interface Service { code: string; description: string; unit: "each" | "hour"; price: number | null; minQuantity?: number }
interface RateSet { id: string; crew_rates: CrewRate[]; services: Service[]; rules: Record<string, unknown>; notes: string | null; updated_at: string }
interface Extra { id: string; code: string | null; description: string; quantity: number; unit: string; unit_price: number | null; source: string; docket_id: string | null; created_at: string }

async function orgId(): Promise<string> {
  const { data: s } = await supabase.auth.getSession();
  const { data, error } = await supabase.from("profiles").select("organisation_id").eq("id", s.session?.user.id ?? "").single();
  if (error) throw error;
  return data.organisation_id as string;
}

async function loadBilling(job: JobDetail) {
  if (PREVIEW) return { rateSet: null, extras: [] as Extra[], signedQuote: false, quoteId: null as string | null };
  const [rs, ex, q] = await Promise.all([
    supabase.from("job_rate_sets").select("id, crew_rates, services, rules, notes, updated_at").eq("job_id", job.id).maybeSingle(),
    supabase.from("job_extras").select("id, code, description, quantity, unit, unit_price, source, docket_id, created_at").eq("job_id", job.id).order("created_at"),
    job.quote_id ? supabase.from("quotes").select("id, status").eq("id", job.quote_id).maybeSingle() : Promise.resolve({ data: null, error: null }),
  ]);
  if (rs.error) throw rs.error;
  if (ex.error) throw ex.error;
  return { rateSet: rs.data as RateSet | null, extras: (ex.data ?? []) as Extra[], signedQuote: (q.data as { status: string } | null)?.status === "accepted", quoteId: job.quote_id ?? null };
}

/** Equipment the docket records that bills as a job extra, so a new rate set has a line for each. */
const docketExtras = (): Service[] =>
  (BRAND.field?.docket.equipment ?? []).filter((e) => e.extraCode).map((e) => ({ code: e.extraCode!, description: e.label, unit: e.unit, price: null }));

async function createRateSet(jobId: string) {
  const card = await currentRateCard();
  if (!card) throw new Error("There is no rate card in effect to start from.");
  const { data: rules } = await supabase.from("rate_cards").select("rules").eq("id", card.id).single();
  const services: Service[] = card.service_prices.map((s) => ({ code: s.code, description: s.description, unit: s.unit, price: Number(s.price), ...(s.min_quantity ? { minQuantity: Number(s.min_quantity) } : {}) }));
  for (const e of docketExtras()) if (!services.some((s) => s.code === e.code)) services.push(e);
  const { error } = await supabase.from("job_rate_sets").insert({
    organisation_id: await orgId(),
    job_id: jobId,
    based_on_rate_card_id: card.id,
    crew_rates: card.rate_card_rows.map((r) => ({ crewSize: r.crew_size, day: Number(r.day_rate), night: Number(r.night_rate), weekend: Number(r.weekend_rate), publicHoliday: Number(r.public_holiday_rate), travelAllowance: Number(r.travel_allowance) })),
    services,
    rules: (rules?.rules as Record<string, unknown>) ?? {},
    notes: `Copied from ${card.name}`,
  });
  if (error) throw error;
}

export function JobBilling({ job }: { job: JobDetail }) {
  const s = useAsync(() => loadBilling(job), [job.id, job.quote_id]);
  if (s.loading && !s.data) return <div className="card"><div className="card__body"><SkeletonLines rows={2} /></div></div>;
  if (s.error) return <div className="card"><div className="card__body"><ErrorState message={s.error} onRetry={s.reload} /></div></div>;
  const { rateSet, extras, signedQuote } = s.data!;
  return (
    <>
      <JobRates jobId={job.id} rateSet={rateSet} signedQuote={signedQuote} onChange={s.reload} />
      <JobExtras jobId={job.id} quoteId={signedQuote ? job.quote_id ?? null : null} extras={extras} services={rateSet?.services ?? []} onChange={s.reload} />
    </>
  );
}

function JobRates({ jobId, rateSet, signedQuote, onChange }: { jobId: string; rateSet: RateSet | null; signedQuote: boolean; onChange: () => void }) {
  const [edit, setEdit] = useState<RateSet | null>(null);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const num = (v: string) => (v.trim() === "" ? null : Math.max(0, Number(v)));

  async function run(fn: () => Promise<void>, done: string) {
    setBusy(true);
    try {
      await fn();
      ok(done);
      setEdit(null);
      onChange();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card__head">
        <h2 className="card__title">Rates for this job</h2>
        {rateSet && !edit && <button type="button" className="btn btn--outline btn--sm" onClick={() => { setNotice(null); setEdit(structuredClone(rateSet)); }}>Edit rates</button>}
      </div>
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        {!rateSet && !signedQuote && (
          <p className="notice notice--warn small" role="status">This job has no signed quote and no job rates. Set up its rates before it is billed.</p>
        )}
        {!rateSet && (
          <div>
            <p className="small muted">{signedQuote ? "Billed from the signed quote's rate card. Set up job rates only to bill at different rates." : "Job rates start as a copy of the rate card in effect today. Change any rate for this job only."}</p>
            <button type="button" className="btn btn--ink btn--sm" disabled={busy} aria-busy={busy} onClick={() => void run(() => createRateSet(jobId), "Job rates set up from the rate card.")}>
              <Icon name="plus" size={16} /> Set up rates from the rate card
            </button>
          </div>
        )}
        {rateSet && !edit && (
          <>
            <div className="table-wrap table-wrap--flat">
              <table className="table table--stack">
                <thead><tr><th scope="col">Crew</th><th scope="col" className="n">Day</th><th scope="col" className="n">Night</th><th scope="col" className="n">Weekend</th><th scope="col" className="n">Public holiday</th><th scope="col" className="n">Travel</th></tr></thead>
                <tbody>
                  {rateSet.crew_rates.map((r) => (
                    <tr key={r.crewSize}>
                      <td data-label="Crew">{r.crewSize}</td>
                      <td data-label="Day" className="n">{aud(r.day)}</td><td data-label="Night" className="n">{aud(r.night)}</td><td data-label="Weekend" className="n">{aud(r.weekend)}</td>
                      <td data-label="Public holiday" className="n">{aud(r.publicHoliday)}</td><td data-label="Travel" className="n">{aud(r.travelAllowance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="list">
              {rateSet.services.map((sv) => (
                <li key={sv.code} className="list__row">
                  <span>{sv.description} <span className="sub">{sv.code}, per {sv.unit}{sv.minQuantity ? `, minimum ${sv.minQuantity}` : ""}</span></span>
                  <span className="list__value">{sv.price === null ? <span className="muted">To confirm</span> : aud(sv.price)}</span>
                </li>
              ))}
            </ul>
            <p className="small muted">Per crew per hour, excluding GST. {rateSet.notes}</p>
          </>
        )}
        {edit && (
          <div className="stack">
            <div className="table-wrap table-wrap--flat">
              <table className="table table--stack">
                <thead><tr><th scope="col">Crew</th><th scope="col">Day</th><th scope="col">Night</th><th scope="col">Weekend</th><th scope="col">Public holiday</th><th scope="col">Travel</th></tr></thead>
                <tbody>
                  {edit.crew_rates.map((r, i) => (
                    <tr key={r.crewSize}>
                      <td data-label="Crew">{r.crewSize}</td>
                      {(["day", "night", "weekend", "publicHoliday", "travelAllowance"] as const).map((k) => (
                        <td key={k} data-label={k}>
                          <input className="input" type="number" step="0.01" min={0} aria-label={`${k} rate for ${r.crewSize}`} value={r[k]} style={{ maxWidth: 110 }}
                            onChange={(e) => setEdit({ ...edit, crew_rates: edit.crew_rates.map((x, j) => (j === i ? { ...x, [k]: num(e.target.value) ?? 0 } : x)) })} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {edit.services.map((sv, i) => (
              <div key={sv.code} className="timerow">
                <span>{sv.description} ({sv.unit})</span>
                <input className="input" type="number" step="0.01" min={0} placeholder="To confirm" aria-label={`Price for ${sv.description}`} value={sv.price ?? ""}
                  onChange={(e) => setEdit({ ...edit, services: edit.services.map((x, j) => (j === i ? { ...x, price: num(e.target.value) } : x)) })} />
              </div>
            ))}
            <div className="form-actions">
              <button type="button" className="btn btn--primary btn--sm" disabled={busy} aria-busy={busy}
                onClick={() => void run(async () => { const { error } = await supabase.from("job_rate_sets").update({ crew_rates: edit.crew_rates, services: edit.services }).eq("id", edit.id); if (error) throw error; }, "Job rates saved.")}>Save rates</button>
              <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={() => setEdit(null)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function JobExtras({ jobId, quoteId, extras, services, onChange }: { jobId: string; quoteId: string | null; extras: Extra[]; services: Service[]; onChange: () => void }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ code: "", description: "", quantity: "1", unit: "each", price: "" });
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();

  async function run(fn: () => Promise<void>, done: string) {
    setBusy(true);
    try {
      await fn();
      ok(done);
      onChange();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function fromQuote() {
    const { data, error } = await supabase.from("quote_lines").select("id, code, description, quantity, unit, unit_price").eq("quote_id", quoteId!).eq("unit", "each");
    if (error) throw error;
    const lines = (data ?? []).filter((l) => !extras.some((x) => x.source === "quote" && x.description === l.description));
    if (!lines.length) throw new Error("The signed quote has no fixed-price lines that are not already here.");
    const org = await orgId();
    const { error: e2 } = await supabase.from("job_extras").insert(lines.map((l) => ({ organisation_id: org, job_id: jobId, code: /^[A-Z][A-Z0-9_]{0,39}$/.test(l.code) ? l.code : null, description: l.description, quantity: l.quantity, unit: l.unit, unit_price: l.unit_price, source: "quote" })));
    if (e2) throw e2;
  }

  return (
    <div className="card">
      <div className="card__head">
        <h2 className="card__title">Extras</h2>
        <span className="actions">
          {quoteId && <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={() => void run(fromQuote, "Added the signed quote's fixed-price lines.")}>From the signed quote</button>}
          {!adding && <button type="button" className="btn btn--outline btn--sm" onClick={() => { setNotice(null); setAdding(true); }}><Icon name="plus" size={16} /> Add an extra</button>}
        </span>
      </div>
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        {adding && (
          <div className="form-grid">
            <label className="field">
              <span className="field__label">From the job's rates</span>
              <select className="input" value={f.code} onChange={(e) => {
                const sv = services.find((x) => x.code === e.target.value);
                setF({ ...f, code: e.target.value, description: sv?.description ?? f.description, unit: sv?.unit ?? f.unit, price: sv?.price != null ? String(sv.price) : "" });
              }}>
                <option value="">Something else</option>
                {services.map((sv) => <option key={sv.code} value={sv.code}>{sv.description}</option>)}
              </select>
            </label>
            <label className="field"><span className="field__label">Description</span><input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
            <label className="field"><span className="field__label">Quantity</span><input className="input" type="number" min={0.25} step={0.25} value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} /></label>
            <label className="field"><span className="field__label">Unit</span><input className="input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></label>
            <label className="field"><span className="field__label">Price each, excluding GST</span><input className="input" type="number" min={0} step={0.01} placeholder="To confirm" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></label>
            <div className="form-actions field--wide">
              <button type="button" className="btn btn--primary btn--sm" disabled={busy || !f.description.trim() || !(Number(f.quantity) > 0)} aria-busy={busy}
                onClick={() => void run(async () => {
                  const { error } = await supabase.from("job_extras").insert({ organisation_id: await orgId(), job_id: jobId, code: f.code || null, description: f.description.trim(), quantity: Number(f.quantity), unit: f.unit.trim() || "each", unit_price: f.price.trim() === "" ? null : Number(f.price), source: "manual" });
                  if (error) throw error;
                  setAdding(false);
                  setF({ code: "", description: "", quantity: "1", unit: "each", price: "" });
                }, "Extra added.")}>Add</button>
              <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        )}
        {extras.length === 0 && !adding && <EmptyState icon="dollar" title="No extras yet">Traffic plans, permits, an arrow board or TMA hire. Equipment on a signed docket is added here automatically.</EmptyState>}
        {extras.length > 0 && (
          <ul className="list">
            {extras.map((x) => (
              <li key={x.id} className="list__row">
                <span>{x.description} <span className="sub">{x.quantity} {x.unit}{x.source === "docket" ? ", from a docket" : x.source === "quote" ? ", from the signed quote" : ""}</span></span>
                <span className="actions">
                  <input className="input" type="number" min={0} step={0.01} placeholder="To confirm" aria-label={`Price for ${x.description}`} defaultValue={x.unit_price ?? ""} style={{ maxWidth: 120 }}
                    onBlur={(e) => {
                      const v = e.target.value.trim() === "" ? null : Number(e.target.value);
                      if (v === x.unit_price) return;
                      void run(async () => { const { error } = await supabase.from("job_extras").update({ unit_price: v }).eq("id", x.id); if (error) throw error; }, "Price saved.");
                    }} />
                  <button type="button" className="btn btn--outline btn--xs" disabled={busy} onClick={() => void run(async () => { const { error } = await supabase.from("job_extras").delete().eq("id", x.id); if (error) throw error; }, "Extra removed.")}>Remove</button>
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="small muted">Extras with no price yet are not billed until a price is entered.</p>
      </div>
    </div>
  );
}
